import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Boxes, Rocket, Mail, CheckCircle2, Upload, ArrowRight, Layers, Ruler,
  ScanLine, Wrench, Palette, Gauge, ShieldCheck, FileBox, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

const IMG = {
  heroMini: "https://images.unsplash.com/photo-1741177479787-f6c63266af14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHszfHwzZCUyMHByaW50aW5nJTIwbW9kZWwlMjByZXNpbiUyMGZpZ3VyZSUyMG1pbmlhdHVyZSUyMHN0dWRpb3xlbnwwfHx8fDE3ODg2NDE4MjZ8MA&ixlib=rb-4.1.0&q=85",
  printerFarm: "https://images.unsplash.com/photo-1611117775350-ac3950990985?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHwzZCUyMHByaW50ZXIlMjBub3p6bGUlMjBmaWxhbWVudCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg4NjQxODI5fDA&ixlib=rb-4.1.0&q=85",
  statue: "https://images.unsplash.com/photo-1678997887858-c383cc8d1791?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHw0fHwzZCUyMHByaW50aW5nJTIwbW9kZWwlMjByZXNpbiUyMGZpZ3VyZSUyMG1pbmlhdHVyZSUyMHN0dWRpb3xlbnwwfHx8fDE3ODg2NDE4MjZ8MA&ixlib=rb-4.1.0&q=85",
};

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }) };

export default function PrintsHomePage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("prints_waitlist_signed") === "yes") setSigned(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/prints/waitlist", { email: email.trim() });
      localStorage.setItem("prints_waitlist_signed", "yes");
      setSigned(true);
      toast.success("You're on the list. We'll ping you at launch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign you up — try again in a bit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(1100px_520px_at_75%_-5%,rgba(244,114,182,0.12),transparent),radial-gradient(900px_460px_at_10%_10%,rgba(34,211,238,0.10),transparent)]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "repeating-linear-gradient(0deg,rgba(255,255,255,0.6) 0,rgba(255,255,255,0.6) 1px,transparent 1px,transparent 4px)" }} />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        {/* ── HERO ── */}
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <motion.div variants={fade} initial="hidden" animate="show" className="flex flex-col items-start gap-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-fuchsia-300/80 border border-fuchsia-300/25 bg-fuchsia-400/5 rounded-full px-3 py-1">On-Demand Forge · Studio-Grade</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[0.03em] text-white leading-[1.04]">
              HIGH-PRECISION 3D PRINTS — <span className="text-fuchsia-300">YOUR FILES</span> OR <span className="text-cyan-300">OUR ART</span>.
            </h1>
            <p className="text-white/60 text-base leading-relaxed max-w-xl">
              We turn digital models into physical, studio-grade objects — resin miniatures, display statues, tabletop terrain and detailed collectibles. Upload an <span className="text-white">STL or OBJ</span> for an instant quote, or pull a figure straight from our art library. Every piece is resin-printed, washed and UV-cured by hand, then <span className="text-white">shipped unpainted in the colors we have on hand</span>.
            </p>

            <div className="mt-1 flex flex-wrap items-center gap-3">
              <Link to="/prints/quote" data-testid="prints-get-quote-cta">
                <Button className="bg-fuchsia-400 text-black hover:bg-fuchsia-300 h-11 px-6 font-semibold">
                  <Upload className="h-4 w-4 mr-1.5" /> Get an instant quote <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/prints/shop" data-testid="prints-shop-cta">
                <Button variant="outline" className="h-11 px-6 font-medium border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10">
                  <Boxes className="h-4 w-4 mr-1.5" /> Browse the shop
                </Button>
              </Link>
            </div>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
              {["Resin (SLA) prints", "Printed in available colors", "Instant mesh analysis", "Hand-washed & UV-cured"].map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                  <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-400" /> {s}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={1} className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[4/5] shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <img src={IMG.heroMini} alt="resin miniature" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent" />
              <div className="absolute inset-0 rounded-3xl" style={{ boxShadow: "inset 0 0 0 1px rgba(244,114,182,0.25), inset 0 0 60px rgba(34,211,238,0.10)" }} />
              <div className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.24em] text-white/70 bg-black/50 backdrop-blur-sm rounded-md px-2 py-1">Resin print · 32mm archer</div>
            </div>
          </motion.div>
        </div>

        {/* ── WAITLIST ── */}
        <div className="mt-10 max-w-xl">
          {signed ? (
            <div data-testid="prints-waitlist-thanks" className="hud-pill px-5 py-4 rounded-2xl flex items-center gap-3 border border-emerald-300/30 bg-emerald-400/5">
              <CheckCircle2 className="h-5 w-5 text-emerald-300" />
              <div>
                <div className="text-sm text-white">You&apos;re on the list.</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">We&apos;ll email you the second the presses go live.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="prints-waitlist-form" className="flex flex-col sm:flex-row items-stretch gap-2">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input data-testid="prints-waitlist-email" type="email" required placeholder="you@somewhere.io" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 h-11" />
              </div>
              <Button type="submit" data-testid="prints-waitlist-submit" disabled={submitting} className="bg-fuchsia-400 text-black hover:bg-fuchsia-300 h-11 px-6 font-medium">
                {submitting ? "Adding…" : "Notify me at launch"}
              </Button>
            </form>
          )}
        </div>

        {/* ── WHAT WE CAN MAKE ── */}
        <SectionHead eyebrow="What we can make" title="FROM 28MM MINIS TO COLLECTOR STATUES" />
        <div className="grid md:grid-cols-3 gap-4">
          {CATEGORIES.map((c, i) => (
            <motion.div key={c.slug} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              data-testid={`prints-preview-${c.slug}`}
              className="group relative rounded-2xl border border-white/10 overflow-hidden bg-[hsl(222_22%_7%)]/80">
              <div className="relative h-40 overflow-hidden">
                <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-100 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0D16] to-transparent" />
                <div className="absolute top-3 left-3 h-9 w-9 rounded-lg border flex items-center justify-center backdrop-blur-sm" style={{ borderColor: `${c.accent}66`, background: `${c.accent}1f` }}>
                  <c.icon className="h-5 w-5" style={{ color: c.accent }} />
                </div>
              </div>
              <div className="p-5">
                <div className="font-display text-lg tracking-[0.12em] text-white">{c.title}</div>
                <div className="mt-1.5 text-xs text-white/55 leading-relaxed">{c.blurb}</div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {c.tags.map((t) => <span key={t} className="font-mono text-[9px] uppercase tracking-[0.18em] rounded px-2 py-0.5 border" style={{ color: c.accent, borderColor: `${c.accent}44`, background: `${c.accent}0f` }}>{t}</span>)}
                </div>
                <Link to={c.to} className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] group-hover:gap-2.5 transition-all" style={{ color: c.accent }}>
                  {c.cta} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── BEFORE YOU PRINT ── */}
        <SectionHead eyebrow="Before you print" title="THE PRE-FLIGHT CHECKLIST" sub="Great prints are won before the printer starts. Here's what to think about so your file comes out crisp — not a blobby, warped mess." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PREFLIGHT.map((p, i) => (
            <motion.div key={p.title} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
              className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/70 p-5 hover:border-fuchsia-300/30 transition-colors">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg border border-cyan-300/40 bg-cyan-400/10 flex items-center justify-center"><p.icon className="h-5 w-5 text-cyan-300" /></div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">{p.num}</div>
              </div>
              <div className="mt-3 font-display text-base tracking-[0.06em] text-white">{p.title}</div>
              <div className="mt-1.5 text-xs text-white/55 leading-relaxed">{p.body}</div>
            </motion.div>
          ))}
        </div>

        {/* ── HOW YOUR PRINT SHIPS (resin-only) ── */}
        <SectionHead eyebrow="What you'll receive" title="RESIN-PRINTED, SHIPPED READY TO PAINT" sub="We print exclusively on resin (SLA) for the sharpest possible detail. Pieces ship clean and unpainted, in whatever print colors we have on hand — perfect as-is for display or as a primed-ready base for your own paint job." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {RECEIVE.map((r) => (
            <div key={r.k} className="rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/70 p-5">
              <div className="h-9 w-9 rounded-lg border border-fuchsia-300/40 bg-fuchsia-400/10 flex items-center justify-center mb-3"><r.icon className="h-5 w-5 text-fuchsia-300" /></div>
              <div className="font-display text-base tracking-[0.06em] text-white">{r.k}</div>
              <div className="mt-1.5 text-xs text-white/55 leading-relaxed">{r.v}</div>
            </div>
          ))}
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">Resin types & finishes — we'll dial these in with you per order.</p>

        {/* ── PIPELINE ── */}
        <SectionHead eyebrow="How it works" title="FOUR STEPS TO A REAL OBJECT" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PIPELINE.map((s, i) => (
            <div key={s.title} className="relative rounded-2xl border border-white/10 bg-[hsl(222_22%_7%)]/70 p-5">
              <div className="font-display text-4xl text-white/10 leading-none">{String(i + 1).padStart(2, "0")}</div>
              <div className="-mt-4 flex items-center gap-2"><s.icon className="h-5 w-5 text-fuchsia-300" /><div className="font-display text-base tracking-[0.06em] text-white">{s.title}</div></div>
              <div className="mt-1.5 text-xs text-white/55 leading-relaxed">{s.body}</div>
            </div>
          ))}
        </div>

        {/* ── FINAL CTA ── */}
        <div className="mt-16 relative rounded-3xl overflow-hidden border border-white/10">
          <img src={IMG.printerFarm} alt="print farm" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05050A] via-[#05050A]/85 to-transparent" />
          <div className="relative p-8 sm:p-12 flex flex-col items-start gap-4 max-w-2xl">
            <Sparkles className="h-5 w-5 text-fuchsia-300" />
            <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[0.04em] text-white">GOT A FILE? SEE YOUR PRICE IN SECONDS.</h3>
            <p className="text-white/60 text-sm leading-relaxed">Drag in an STL or OBJ and our engine measures volume, overhangs and print time on the spot. No account, no commitment — just a number.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/prints/quote"><Button className="bg-fuchsia-400 text-black hover:bg-fuchsia-300 h-11 px-6 font-semibold"><Upload className="h-4 w-4 mr-1.5" /> Upload & quote</Button></Link>
              <Link to="/prints/shop"><Button variant="outline" className="h-11 px-6 border-cyan-400/60 text-cyan-300 hover:bg-cyan-400/10"><Boxes className="h-4 w-4 mr-1.5" /> Browse ready-made</Button></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="mt-20 mb-6 max-w-2xl">
      <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-fuchsia-300/80">{eyebrow}</span>
      <h2 className="mt-2 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-[0.04em] text-white">{title}</h2>
      {sub && <p className="mt-2 text-white/55 text-sm leading-relaxed">{sub}</p>}
    </div>
  );
}

const CATEGORIES = [
  { slug: "byo-file", title: "BRING YOUR OWN FILE", accent: "#22d3ee", icon: FileBox, img: IMG.printerFarm, to: "/prints/quote", cta: "Get instant quote",
    blurb: "Upload an STL or OBJ. We analyze volume & overhangs, quote material + color, then print, cure and ship it. Yours end-to-end.", tags: ["STL", "OBJ", "Instant price"] },
  { slug: "playden-art", title: "PLAYDEN CARD FIGURES", accent: "#f472b6", icon: Boxes, img: IMG.heroMini, to: "/prints/shop", cta: "Browse figures",
    blurb: "Tabletop-scale minis & busts pulled from the Playden art library. 28 / 32 / 75mm resin, shipped unpainted in available colors.", tags: ["28–75mm", "Resin", "Unpainted"] },
  { slug: "dioramas", title: "DIORAMAS & CUSTOMS", accent: "#fbbf24", icon: Rocket, img: IMG.statue, to: "/prints/quote", cta: "Commission a build",
    blurb: "Multi-part display scenes, custom bases and bespoke commissions. Resin-printed, shipped unpainted. Limited monthly slots.", tags: ["Multi-part", "Resin", "Bespoke"] },
];

const PREFLIGHT = [
  { num: "01", title: "Geometry & Topology", icon: ScanLine, body: "Send a manifold, watertight mesh (STL / OBJ / 3MF). No holes, no flipped normals, no floating shells — a bad mesh can't be printed cleanly." },
  { num: "02", title: "Wall Thickness & Scale", icon: Ruler, body: "Keep walls ≥1.2mm so resin parts don't collapse or warp in the wash & cure. Tiny raised details vanish below ~0.4mm — size the model for the scale you want." },
  { num: "03", title: "Detail & Durability", icon: Layers, body: "Resin captures crisp faces and fine texture beautifully, but cured resin is rigid and can be brittle — thicken thin spikes, blades and fingers so they survive shipping." },
  { num: "04", title: "Overhangs & Supports", icon: Wrench, body: "Anything past ~45° needs supports. Micro-tip contacts (0.3mm) keep surfaces clean, but plan orientation to hide any support scars." },
  { num: "05", title: "Clearance & Tolerances", icon: Gauge, body: "For moving or snap-fit parts, leave ~0.25mm of gap between mating surfaces so pieces actually fit once cured." },
  { num: "06", title: "Finishing", icon: Palette, body: "Every print is IPA-washed and UV-cured, then shipped unpainted in the print colors we have on hand — ready for display or your own paint." },
];

const RECEIVE = [
  { k: "Resin only", v: "We print exclusively on resin (SLA) for the sharpest detail — no filament/FDM.", icon: Layers },
  { k: "Unpainted", v: "Pieces arrive clean and raw, so you can prime and paint them your way.", icon: Palette },
  { k: "In-stock colors", v: "Printed in whatever resin colors we currently have on hand.", icon: ScanLine },
  { k: "Washed & cured", v: "Hand-washed in IPA and fully UV-cured before we pack and ship.", icon: ShieldCheck },
];

const PIPELINE = [
  { title: "Upload or choose", icon: Upload, body: "Drag & drop your STL/OBJ, or pick a Playden model from the shop." },
  { title: "Auto pre-flight", icon: ScanLine, body: "We measure volume, overhangs & print time and return an instant quote." },
  { title: "Print & cure", icon: Layers, body: "Printed on calibrated resin (SLA), washed in IPA and fully UV-cured." },
  { title: "Pack & ship", icon: ShieldCheck, body: "Shipped unpainted in available colors, packed safe and sent to your door." },
];
